import { useEffect, useState } from "react";
import {
  Box,
  Button,
  ButtonBase,
  Grid,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";

import moment from "moment";
import "moment-timezone";

import makeStyles from "@mui/styles/makeStyles";
import {
  BCPCalendarWeek,
  BCPCircleInfo,
  BCPClock,
  BCPFlag,
  BCPLayerGroup,
  BCPLocationDot,
  BCPTicket,
  BCPUserGroup,
} from "../icons/bcp/BCPIcons";
import { getData } from "../utilities/UtilityFunctions";
import { getEnvironmentVariable } from "../../global/environmentVariables";
import OrganizationCardSmall from "./OrganizationCardSmall";
import UserCardSmall from "./UserCardSmall";

const useStyles = makeStyles((theme) => ({
  eventImgWrapper: {
    maxHeight: "500px",
    "@media (max-width: 901px)": {
      maxHeight: "250px",
    },
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 0,
  },
  eventImg: {
    maxWidth: "100%",
    maxHeight: "500px",
    "@media (max-width: 901px)": {
      maxHeight: "250px",
    },
  },
}));

export default function EventCardFeatured(props) {
  const { event, gameSystemDict } = props;
  const classes = useStyles();
  const [photo, setPhoto] = useState(null);

  const theme = useTheme();
  const mode = theme.palette.mode;

  useEffect(() => {
    const check = async () => {
      setPhoto(null);
      if (event?.photoFile?.cloudFrontDomain) {
        setPhoto(event.photoFile);
      } else if (event?.photoFileId) {
        try {
          var apiPhoto = await getData(`/files/${event.photoFileId}`, {
            queryStringParameters: {},
          });
          setPhoto(apiPhoto);
        } catch (err) {
          console.error(err);
        }
      }
    };
    check();
    // eslint-disable-next-line
  }, [props]);

  return (
    <ButtonBase
      href={`/event/${event.id}`}
      sx={{
        borderRadius: 1,
        boxShadow: 1,
        overflow: "hidden",
        borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
        backgroundColor: "background.secondary",
      }}
    >
      <Grid
        container
        sx={{
          spacing: 2,
          display: "flex",
          justifyContent: "space-between",
          backgroundColor:
            mode === "dark" ? "background.tertiary" : "background.secondary",
          flexDirection: ["column-reverse", "column-reverse", "row"],
        }}
      >
        <Grid
          item
          xs={12}
          md={5}
          sx={{
            "& .MuiTypography-root": {
              overflowWrap: "break-word",
              wordWrap: "break-word",
            },
            zIndex: 1,
            p: [2, 2, 4],
            pt: [2, 2, 4],
          }}
        >
          <Grid container spacing={1}>
            <Grid item xs={12}>
              <Typography variant="h3" sx={{ fontWeight: "700" }}>
                {event.name}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              {Boolean(
                event.gameSystemName ||
                  event.gameSystem ||
                  gameSystemDict?.[event.gameSystemId]?.name
              ) && (
                <Typography sx={{ fontWeight: "700" }}>
                  {event.gameSystemName ||
                    event.gameSystem?.name ||
                    gameSystemDict?.[event.gameSystemId]?.name}
                </Typography>
              )}
            </Grid>

            {/* TODO: Need to update these components once API endpoints are available */}
            <Grid item xs={12}>
              {event?.gameStore?.id ? (
                <OrganizationCardSmall
                  organization={event.gameStore}
                  buttonSx={{ border: "", ml: -2 }}
                />
              ) : (
                <UserCardSmall
                  user={event?.owner}
                  buttonSx={{ border: "", ml: -2 }}
                />
              )}
            </Grid>

            <Grid item xs={12}>
              <Typography gutterBottom>
                <Box
                  sx={{
                    minWidth: "35px",
                    verticalAlign: "bottom",
                    display: "inline-flex",
                  }}
                >
                  <Tooltip title="Event Date">
                    <BCPCalendarWeek />
                  </Tooltip>
                </Box>
                {event.timeZone || event.location.timeZone
                  ? moment(event.eventDate)
                      .tz(event.timeZone || event.location.timeZone)
                      .format("LL")
                  : moment(event.eventDate).format("LL")}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography gutterBottom>
                <Box
                  sx={{
                    minWidth: "35px",
                    verticalAlign: "bottom",
                    display: "inline-flex",
                  }}
                >
                  <Tooltip title="Event Time">
                    <BCPClock />
                  </Tooltip>
                </Box>
                {event.timeZone || event.location.timeZone
                  ? `${moment(event.eventDate)
                      .tz(event.timeZone || event.location.timeZone)
                      .format("LT")} - ${moment(event.eventEndDate)
                      .tz(event.timeZone || event.location.timeZone)
                      .format("LT")}`
                  : `${moment(event.eventDate).format("LT")} - ${moment(
                      event.eventEndDate
                    ).format("LT")}`}
              </Typography>
            </Grid>

            {(event.isOnlineEvent ||
              Boolean(
                event.city || event.zip || event.state || event.country
              )) && (
              <Grid item xs={12}>
                <Typography
                  gutterBottom
                  sx={{
                    display: "flex",
                  }}
                >
                  <Box
                    sx={{
                      minWidth: "35px",
                      verticalAlign: "bottom",
                      display: "inline-flex",
                    }}
                  >
                    <Tooltip title="Event Location">
                      <BCPLocationDot />
                    </Tooltip>
                  </Box>
                  {event.isOnlineEvent
                    ? "Online Event"
                    : event?.formatted_address ||
                      `${event.city ? `${event.city}, ` : ""}${event.state} ${
                        event.country
                      }`.trim()}
                </Typography>
              </Grid>
            )}
            {Boolean(event.eventFormat) && (
              <Grid item xs={12}>
                <Typography gutterBottom>
                  <Box
                    sx={{
                      minWidth: "35px",
                      verticalAlign: "bottom",
                      display: "inline-flex",
                    }}
                  >
                    <Tooltip
                      title={
                        event?.gameSystem?.eventFormats?.label || "Event Format"
                      }
                    >
                      <BCPFlag />
                    </Tooltip>
                  </Box>
                  {event.eventFormat}
                </Typography>
              </Grid>
            )}
            {event.numberOfRounds && event.numberOfRounds > 0 ? (
              <Grid item xs={12}>
                <Typography gutterBottom>
                  <Box
                    sx={{
                      minWidth: "35px",
                      verticalAlign: "bottom",
                      display: "inline-flex",
                    }}
                  >
                    <Tooltip title="Number of Rounds">
                      <BCPLayerGroup />
                    </Tooltip>
                  </Box>
                  {event.numberOfRounds} Rounds
                </Typography>
              </Grid>
            ) : (
              <></>
            )}
            {!event.hidePlayerCount &&
              (event.numTickets > 0 || event.totalPlayers > 0) && (
                <Grid item xs={12}>
                  <Typography gutterBottom>
                    <Box
                      sx={{
                        minWidth: "35px",
                        verticalAlign: "bottom",
                        display: "inline-flex",
                      }}
                    >
                      <Tooltip title="Number of Players">
                        <BCPUserGroup />
                      </Tooltip>
                    </Box>
                    {`${
                      event.ended || event.started
                        ? // && event.currentRound > 0
                          event.checkedInPlayers
                        : event.totalPlayers
                    }${
                      !event.started &&
                      // || !event.currentRound
                      event.numTickets > 0
                        ? ` / ${event.numTickets}`
                        : ""
                    }`}
                  </Typography>
                </Grid>
              )}

            {(event?.ticketing?.useTicketing ||
              (event?.numTickets > 0 &&
                event?.totalPlayers < event?.numTickets &&
                !event?.started &&
                !event?.ended &&
                event?.usingOnlineReg)) && (
              <Grid item xs={6}>
                <Button
                  variant="contained"
                  href={`/event/${event.id}?register=true`}
                  startIcon={<BCPTicket />}
                  fullWidth
                >
                  Register
                </Button>
              </Grid>
            )}
            <Grid item xs={6}>
              <Button
                variant="outlined"
                href={`/event/${event.id}?active_tab=overview`}
                startIcon={<BCPCircleInfo />}
                fullWidth
              >
                Learn More
              </Button>
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={12} md={7} className={classes.eventImgWrapper}>
          <Box
            className={classes.eventImgWrapper}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              maxWidth: "100%",
              minWidth: "100%",
              paddingTop: "0px !important",
              "&:hover": {
                opacity: [0.9, 0.8, 0.7],
                transition: "opacity 0.2s",
              },
            }}
          >
            <img
              src={
                photo?.url ||
                event?.photoUrl ||
                `${getEnvironmentVariable(
                  "SYSTEM_IMAGES_CLOUDFRONT"
                )}/images/EventImage${
                  theme.palette.mode === "dark" ? "Dark" : ""
                }.png`
              }
              alt={event.name}
              className={classes.eventImg}
              style={{
                position: "absolute",
                width: "-webkit-fill-available",
                height: "-webkit-fill-available",
                filter: "blur(8px)",
                borderRadius: "8px",
              }}
            />
            <img
              src={
                photo?.url ||
                event?.photoUrl ||
                `${getEnvironmentVariable(
                  "SYSTEM_IMAGES_CLOUDFRONT"
                )}/images/EventImage${
                  theme.palette.mode === "dark" ? "Dark" : ""
                }.png`
              }
              alt={event.name}
              className={classes.eventImg}
              style={{
                "z-index": "1",
              }}
            />
          </Box>
        </Grid>
      </Grid>
    </ButtonBase>
  );
}
